import {
  Class,
  Entity,
  EntitySearchType,
  type GraphEntityType,
  type GraphRelationDirection,
  type IntersectionsV5Response,
  ontology,
  RelationKind,
  type RelationsV5Response,
} from '@api';
import { postEntitiesByQuery } from '@terminal/services/entities/entity-service';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';

export const DEFAULT_GRAPH_NODE_ID = 'nvda_nasd';
export const CLASS_STOCK = Class.ClassStock;
const ENTITY_LOOKUP_LIMIT = 30;
const US_COUNTRY_LONG = 'us';

export type GraphLandingFallback = {
  nodeId: string;
  nodeType: GraphEntityType;
};

export type FallbackBucket = 'entityLookup' | 'followedThemes';

export const selectRandomItem = <T>(items: T[]): T | undefined =>
  items[Math.floor(Math.random() * items.length)];

export const selectFallbackBucket = (
  followedThemeIds: readonly string[]
): FallbackBucket => {
  if (followedThemeIds.length === 0) {
    return 'entityLookup';
  }

  return Math.random() < 0.5 ? 'entityLookup' : 'followedThemes';
};

export const isUsEntity = (entity: Entity) =>
  entity.country?.long === US_COUNTRY_LONG;

export const isPrimaryEntity = (entity: Entity) =>
  entity.primary_tag === entity.tag || entity.primary_tag === '';

export const pickRandomUsEntityTag = async () => {
  const { data } = await postEntitiesByQuery(
    '',
    EntitySearchType.EntityLookup,
    {
      limit: ENTITY_LOOKUP_LIMIT,
      criteria: {
        asset_class: [],
        class: [CLASS_STOCK],
        sub_class: [],
      },
    },
    true
  );

  if (data) {
    const entities = data.filter(
      entity => isUsEntity(entity) && isPrimaryEntity(entity)
    );
    const picked = selectRandomItem(entities);
    if (picked) {
      return picked.tag;
    }
  }

  return undefined;
};

export const resolveGraphLandingFallback = async ({
  followedThemeIds,
}: {
  followedThemeIds: string[];
}): Promise<GraphLandingFallback> => {
  const bucket = selectFallbackBucket(followedThemeIds);

  if (bucket === 'followedThemes') {
    const themeId = selectRandomItem(followedThemeIds);
    if (themeId) {
      return { nodeId: themeId, nodeType: 'theme' };
    }
  }

  const entityTag = await pickRandomUsEntityTag();

  return {
    nodeId: entityTag ?? DEFAULT_GRAPH_NODE_ID,
    nodeType: 'company',
  };
};

export interface GetGraphRelationsOptions {
  nodeType: GraphEntityType;
  rawNodeId: string;
  maxRank?: number;
  signal?: AbortSignal;
  direction?: GraphRelationDirection;
  relationKind?: RelationKind;
}

export const getGraphRelations = ({
  nodeType,
  rawNodeId,
  maxRank,
  direction = 'outbound',
  relationKind,
  signal,
}: GetGraphRelationsOptions): Promise<RelationsV5Response> =>
  wretchRequest(
    ontology.getRelations.path({
      nodeType,
      node: rawNodeId,
      maxRank,
      direction,
      relationKind,
    }),
    {
      schema: ontology.getRelations.schema,
      signal,
    }
  );

export interface GetGraphIntersectionsOptions {
  node: string;
  nodeType: GraphEntityType;
  targetRelation: string;
  limit?: number;
  signal?: AbortSignal;
}

export const getGraphIntersections = ({
  node,
  nodeType,
  targetRelation,
  limit,
  signal,
}: GetGraphIntersectionsOptions): Promise<IntersectionsV5Response> =>
  wretchRequest(
    ontology.getIntersections.path({
      node,
      nodeType,
      targetRelation,
      limit,
    }),
    {
      schema: ontology.getIntersections.schema,
      signal,
    }
  );
